import { DateTime } from 'luxon';
import { getLuxonLocale } from './locale_utils';

export function formatDateFromTs(ms: number, options: { format?: any; timezone?: string; locale?: string } = {}): string {
  const { format = DateTime.DATETIME_SHORT, timezone, locale = getLuxonLocale() } = options;

  if (timezone) {
    return DateTime.fromMillis(ms).setZone(timezone).setLocale(locale).toLocaleString(format);
  }

  return DateTime.fromMillis(ms).setLocale(locale).toLocaleString(format);
}

export function formatDateFromIso(date: string, options: { format?: any; timezone?: string; locale?: string } = {}): string {
  const { format = DateTime.DATETIME_SHORT, timezone, locale = getLuxonLocale() } = options;

  if (timezone) {
    return DateTime.fromISO(date).setZone(timezone).setLocale(locale).toLocaleString(format);
  }

  return DateTime.fromISO(date).setLocale(locale).toLocaleString(format);
}

export function toIsoFromTs(ms: number): string {
  return DateTime.fromMillis(ms).toISO();
}

export function formatUtcFromTs(ms?: number | null, format: string = 'yyyy-LL-dd HH:mm:ss'): string | null {
  if (!ms) return null;
  const dt = DateTime.fromMillis(ms, { zone: 'utc' });
  return dt.isValid ? `${dt.toFormat(format)} UTC` : null;
}

// The formats of the list tables: "Oct 12, 2026, 18:31", or "12 oct 2026, 18:31" in Spanish.
const DATE_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const DAY_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

type DateInput = number | string | DateTime;

function toDateTime(value: DateInput, timezone?: string): DateTime {
  let dt: DateTime;
  if (DateTime.isDateTime(value)) dt = value;
  else if (typeof value === 'number') dt = DateTime.fromMillis(value);
  else dt = DateTime.fromISO(value);
  if (timezone) dt = dt.setZone(timezone);
  return dt.setLocale(getLuxonLocale());
}

export function formatListDate(value: DateInput, timezone?: string): string {
  return toDateTime(value, timezone).toLocaleString(DATE_FORMAT);
}

export function formatListDateTime(value: DateInput, timezone?: string): string {
  const dt = toDateTime(value, timezone);
  return `${dt.toLocaleString(DATE_FORMAT)}, ${dt.toLocaleString(TIME_FORMAT)}`;
}

// Shows the date once when both ends share it, and the year once when they share it:
// "Oct 12, 2026, 00:00 – 23:55", "Oct 12, 00:00 – Oct 14, 2026, 23:55"
export function formatListDateRange(from: DateInput, to: DateInput, timezone?: string): string {
  const start = toDateTime(from, timezone);
  const end = toDateTime(to, timezone);
  const endText = formatListDateTime(end);
  if (start.hasSame(end, 'day')) {
    return `${start.toLocaleString(DATE_FORMAT)}, ${start.toLocaleString(TIME_FORMAT)} – ${end.toLocaleString(TIME_FORMAT)}`;
  }
  const startDate = start.hasSame(end, 'year') ? start.toLocaleString(DAY_FORMAT) : start.toLocaleString(DATE_FORMAT);
  return `${startDate}, ${start.toLocaleString(TIME_FORMAT)} – ${endText}`;
}
